import { Description, ErrorMessage, FieldError, Label, NumberField } from "@heroui/react";
import type { ReactNode } from "react";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { clsx } from "@/src/common/utils/clsx";

interface NumberInputGroupProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
	control: Control<TFieldValues>;
	name: TName;
	label?: ReactNode;
	help?: ReactNode;
	isRequired?: boolean;
	className?: string;
	minValue?: number;
	maxValue?: number;
	step?: number;
	formatOptions?: Intl.NumberFormatOptions;
}

/**
 * RHF-aware numeric input: HeroUI NumberField wired to react-hook-form.
 * Bridges `null` (schema) ↔ `undefined` (NumberField) so empty input stores `null`.
 * Use instead of InputGroup whenever the schema field is `z.number()`.
 */
export function NumberInputGroup<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
	control,
	name,
	label,
	help,
	isRequired,
	className,
	minValue,
	maxValue,
	step,
	formatOptions,
}: NumberInputGroupProps<TFieldValues, TName>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => {
				const numericValue = field.value == null || field.value === "" ? undefined : Number(field.value);
				return (
					<NumberField
						className={clsx("flex w-full flex-col gap-1.5", className)}
						isInvalid={Boolean(fieldState.error)}
						isRequired={isRequired}
						value={Number.isFinite(numericValue) ? numericValue : undefined}
						onChange={(v) => field.onChange(v ?? null)}
						onBlur={field.onBlur}
						name={field.name}
						minValue={minValue}
						maxValue={maxValue}
						step={step}
						formatOptions={formatOptions}
					>
						{label ? <Label>{label}</Label> : null}
						<NumberField.Group>
							<NumberField.DecrementButton />
							<NumberField.Input ref={field.ref} />
							<NumberField.IncrementButton />
						</NumberField.Group>
						{help ? <Description>{help}</Description> : null}
						<FieldError>
							<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
						</FieldError>
					</NumberField>
				);
			}}
		/>
	);
}
